import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, ClockCountdownIcon, PlusIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useCreateTimeBlockNote } from '@/api/reservations/create-time-block-note'
import { useDeleteTimeBlockNote } from '@/api/reservations/delete-time-block-note'
import { TimeBlockNote, useGetTimeBlockNotes } from '@/api/reservations/get-time-block-notes'
import { useUpdateTimeBlockNote } from '@/api/reservations/update-time-block-note'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { EmptyState } from '@/components/ui/empty-state'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { RadialLoader } from '@/components/ui/loaders/radial-loader'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { selectUserData } from '@/reducers/userDataReducer'
import { getApiErrorMessage } from '@/utils'

import { TimeBlockNoteForm } from './time-block-note-form'
import { TimeBlockNoteItem } from './time-block-note-item'
import {
  makeNoteFormSchema,
  NoteFormData,
  noteFormDefaultValues,
} from './time-block-notes-modal.constants'
import * as S from './time-block-notes-modal.styled'

interface TimeBlockNotesModalProps {
  timeBlockId: number
  timezone: string
  onClose: VoidFunction
}

export const TimeBlockNotesModal = ({
  timeBlockId,
  timezone,
  onClose,
}: TimeBlockNotesModalProps) => {
  const { t } = useTranslation(['reservations', 'common'])
  const userData = useSelector(selectUserData)
  const isAdmin = userData?.user?.role === 'Administrator'

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [noteToEdit, setNoteToEdit] = useState<TimeBlockNote | null>(null)

  const { data: notes = [], isLoading } = useGetTimeBlockNotes(timeBlockId)
  const { mutateAsync: createNote, isPending: isCreating } = useCreateTimeBlockNote()
  const { mutateAsync: updateNote, isPending: isUpdating } = useUpdateTimeBlockNote()
  const { mutateAsync: deleteNote } = useDeleteTimeBlockNote()

  const noteFormSchema = useMemo(() => makeNoteFormSchema(t), [t])

  const form = useForm<NoteFormData>({
    defaultValues: noteFormDefaultValues,
    mode: 'onTouched',
    resolver: zodResolver(noteFormSchema),
  })

  const isSaving = isCreating || isUpdating

  const openForm = (note: TimeBlockNote | null) => {
    setNoteToEdit(note)
    form.reset(note ? { noteBody: note.noteBody, title: note.title } : noteFormDefaultValues)
    setIsFormOpen(true)
  }

  const handleCancel = () => {
    setNoteToEdit(null)
    form.reset(noteFormDefaultValues)
    setIsFormOpen(false)
  }

  const onSubmit: SubmitHandler<NoteFormData> = async (data) => {
    try {
      if (noteToEdit) {
        await updateNote({ noteId: noteToEdit.id, timeBlockId, ...data })
        toastFactory.success(t('reservations:timeBlocks.notesModal.toasts.updateSuccess'))
      } else {
        await createNote({ timeBlockId, ...data })
        toastFactory.success(t('reservations:timeBlocks.notesModal.toasts.createSuccess'))
      }
      handleCancel()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const save = useFormSave({
    form,
    isEdit: !!noteToEdit,
    onSubmit,
    onUnchanged: handleCancel,
  })

  const handleDelete = (note: TimeBlockNote) => {
    popupFactory.open(
      ConfirmationPopup,
      {
        cancelText: t('common:actions.cancel'),
        children: (
          <Typography>
            {t('reservations:timeBlocks.notesModal.deleteConfirm.body', { title: note.title })}
          </Typography>
        ),
        confirmText: t('common:actions.delete'),
        onConfirm: async () => {
          try {
            await deleteNote({ noteId: note.id, timeBlockId })
            toastFactory.success(
              t('reservations:timeBlocks.notesModal.deleteConfirm.toasts.success'),
            )
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
        title: t('reservations:timeBlocks.notesModal.deleteConfirm.title'),
        variant: 'danger',
      },
      { suspendCurrent: true },
    )
  }

  const canManageNote = (note: TimeBlockNote) =>
    isAdmin || (!!note.createdBy && userData?.user?.id === note.createdBy.id)

  const footer = (
    <Flex align="center" flex={1} gap={12} justify="space-between">
      <Button disabled={isFormOpen} use="secondary" onClick={() => openForm(null)}>
        {t('reservations:timeBlocks.notesModal.actions.addNote')}
        <Icon icon={PlusIcon} />
      </Button>
      <Button use="secondary" onClick={onClose}>
        {t('common:actions.close')}
      </Button>
    </Flex>
  )

  return (
    <Modal
      footer={footer}
      headerIcon={ClockCountdownIcon}
      style={{ maxHeight: '74.8rem', minHeight: '74.8rem' }}
      title={t('reservations:timeBlocks.notesModal.title')}
      onClose={onClose}
    >
      <Flex direction="column" flex={1} gap={16}>
        {isFormOpen && (
          <S.FormCard>
            <FormProvider {...form}>
              <Flex direction="column" gap={24}>
                <TimeBlockNoteForm />
              </Flex>
            </FormProvider>
            <Flex gap={12} justify="flex-end">
              <Button size="small" use="secondary" onClick={handleCancel}>
                {t('common:actions.cancel')}
              </Button>
              <Button loading={isSaving} size="small" use="primary" onClick={save}>
                {t('reservations:timeBlocks.notesModal.actions.saveNote')}
                <Icon icon={CheckIcon} />
              </Button>
            </Flex>
          </S.FormCard>
        )}
        {isLoading ? (
          <Flex align="center" flex={1} justify="center">
            <RadialLoader />
          </Flex>
        ) : notes.length === 0 ? (
          !isFormOpen && (
            <Flex align="center" flex={1} justify="center">
              <EmptyState
                description={t('reservations:timeBlocks.notesModal.empty.description')}
                image={{
                  alt: t('reservations:timeBlocks.notesModal.empty.imageAlt'),
                  src: '/images/no-notes.svg',
                  width: '8rem',
                }}
                title={t('reservations:timeBlocks.notesModal.empty.title')}
              />
            </Flex>
          )
        ) : (
          notes.map((note) => (
            <TimeBlockNoteItem
              key={note.id}
              canManage={canManageNote(note)}
              note={note}
              timezone={timezone}
              onDelete={handleDelete}
              onEdit={openForm}
            />
          ))
        )}
      </Flex>
    </Modal>
  )
}
